import { yupResolver } from '@hookform/resolvers/yup';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Grid, IconButton, InputAdornment, TextField } from '@mui/material';
import { ChangeEvent, FormEvent, useState } from 'react';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import * as yup from 'yup';

import { Button, PasswordStrengthMeter } from '@bai/components';
import { setUserRole } from '@bai/features/user';
import { RoleNames } from '@bai/utils/types';

import {
  LoginFeatureResponse,
  loginFeatureValidator,
} from './loginFeatureSubmitHandler';
import { ValidationMessageType, ValidationType } from './loginFeatureTypes';

interface NewPasswordProps {
  primaryColor?: string;
  logo?: string;
  handleCancel: () => void;
  handleSubmit: (
    event: FormEvent,
    endpoint: string,
    password: string | null,
    username: string | null,
  ) => Promise<LoginFeatureResponse | string>;
  username: string;
  hasCustomLogo: boolean;
  hasWeakPassword?: boolean;
}

export function NewPassword({
  primaryColor,
  logo,
  handleCancel,
  handleSubmit,
  username,
  hasCustomLogo,
  hasWeakPassword,
}: NewPasswordProps) {
  const navigate = useNavigate();
  const endpoint = 'api/account/savepassword';
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [newPasswordValid, setNewPasswordValid] = useState(true);
  const [repeatPasswordValid, setRepeatPasswordValid] = useState(true);
  const [validStrength, setValidStrength] = useState(false);
  const [newPasswordError, setNewPasswordError] = useState(
    `Password ${ValidationMessageType.IS_REQUIRED}`,
  );
  const [repeatPasswordError, setRepeatPasswordError] = useState(
    `Password ${ValidationMessageType.IS_REQUIRED}`,
  );

  const allValidationsPassed =
    newPasswordValid && repeatPasswordValid && validStrength;

  const handleNewPasswordCheck = () => {
    if (newPassword.length === 0) {
      setNewPasswordValid(false);
      setNewPasswordError(`Password ${ValidationMessageType.IS_REQUIRED}`);
    } else if (newPassword.length > 0) {
      setNewPasswordValid(
        loginFeatureValidator(
          ValidationType.STRENGTH_MET,
          10,
          newPassword,
          null,
        ),
      );
      if (!newPasswordValid) {
        setNewPasswordError(ValidationMessageType.STRENGTH_NOT_MET);
      }
    }

    if (repeatPassword !== '') {
      setRepeatPasswordValid(
        loginFeatureValidator(
          ValidationType.VALUES_MATCH,
          null,
          newPassword,
          repeatPassword,
        ),
      );
    }
  };

  const handleRepeatPasswordCheck = () => {
    if (repeatPassword.length === 0) {
      setRepeatPasswordValid(false);

      setRepeatPasswordError(`Password ${ValidationMessageType.IS_REQUIRED}`);
    } else if (repeatPassword.length > 0) {
      setRepeatPasswordValid(
        loginFeatureValidator(
          ValidationType.VALUES_MATCH,
          null,
          newPassword,
          repeatPassword,
        ),
      );

      if (!repeatPasswordValid) {
        setRepeatPasswordError(ValidationMessageType.VALUES_MUST_MATCH);
      }
    }

    if (newPassword !== '') {
      setNewPasswordValid(
        loginFeatureValidator(
          ValidationType.STRENGTH_MET,
          10,
          newPassword,
          null,
        ),
      );
    }
  };

  const handleValidation = () => {
    handleNewPasswordCheck();
    handleRepeatPasswordCheck();
  };

  const handleNewPasswordChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setNewPassword(e.target.value.trim());
  };

  const handleRepeatPasswordChange = async (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setRepeatPassword(e.target.value.trim());
  };

  const handleClickShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleClickShowRepeatPassword = () => {
    setShowRepeatPassword(!showRepeatPassword);
  };

  // yup.Reference<unknown> | yup.ISchema<string, yup.ObjectSchema<{ newPassword: string; repeatPassword: string; }, yup.AnyObject, any, ''>, any, any>

  // See https://github.com/react-hook-form/resolvers/issues/643
  const schema: (
    strengthValue: boolean,
  ) => yup.ObjectSchema<
    { newPassword: string; repeatPassword: string },
    yup.AnyObject,
    { newPassword: undefined; repeatPassword: undefined },
    ''
  > = (strengthValue: boolean) =>
    yup
      .object({
        newPassword: yup
          .string()
          .min(10, ValidationMessageType.STRENGTH_NOT_MET)
          .required(`Password ${ValidationMessageType.IS_REQUIRED}`)
          .test(
            'validStrength',
            ValidationMessageType.WEAK_PASSWORD,
            () => strengthValue === true,
          ),
        repeatPassword: yup
          .string()
          .oneOf([yup.ref('newPassword')], 'Passwords must match')
          .required(ValidationMessageType.VALUES_MUST_MATCH),
      })
      .required();

  const { reset } = useForm({
    defaultValues: { newPassword: '', repeatPassword: '' },

    // @ts-ignore
    resolver: yupResolver(
      schema(validStrength),

      // {} as yup.ValidateOptions,
      // { mode: 'async', raw: true },
      // undefined, undefined, undefined
    ),
    /** Note: Without the @ts-ignore above, running yarn install results in an error:
     * TS2554: Expected 6 arguments, but got 1.
     * If 6 argumemtns are added, then the linter has an error:
     * Expected 1-3 arguments, but got 6.ts(2554)
     * So the typescript validation conflicts with the linter validation.
     * The documentation shows only a single argument provided to yupResolver
     * https://www.npmjs.com/package/@hookform/resolvers
     * and even if I look at the yupResolver code in yup.d.ts,
     * it shows only 3 parameters, with the first being required the second and third optional,
     * So I assume that the TypeScript checking is wrong, and thus I added @ts-ignore
     */
  });

  const handleSubmitResponse = async (event: FormEvent) => {
    event.preventDefault();
    handleValidation();

    if (username === '' || newPassword === '' || !allValidationsPassed) {
      return;
    }

    const newPassResp: LoginFeatureResponse | number = (await handleSubmit(
      event,
      endpoint,
      newPassword,
      username,
    )) as LoginFeatureResponse | number;

    if (typeof newPassResp === 'object' && newPassResp.StatusCode === 200) {
      try {
        await setUserRole(RoleNames.Student);
      } catch {
        console.error('Failed to set user role after password reset.');
        // Legacy AngularJS user service may not be initialized; navigation proceeds regardless
      }
      void navigate('/student/home/');
    }

    reset();
  };

  const renderStrengthMeter = (validateThis: string) => (
    <div className="progress-wrapper">
      <PasswordStrengthMeter
        password={validateThis}
        handleScoreRequirementMet={setValidStrength}
      />
    </div>
  );

  return (
    <form
      onSubmit={(event: React.FormEvent<HTMLFormElement>) =>
        handleSubmitResponse(event)
      }
    >
      <Grid>
        <Grid
          container
          sx={{
            justifyContent: 'center',
            margin: '0 auto',
            width: hasCustomLogo ? '40%' : '60%',
          }}
        >
          <img src={logo} alt="logo" className="themed_logo" data-cy="logo" />
        </Grid>
        {hasWeakPassword && (
          <Grid
            container
            className="alert alert-danger"
            sx={{ alignItems: 'center' }}
          >
            Your current password is considered weak. Please update your
            password below.
          </Grid>
        )}

        <h1>Enter New Password</h1>

        <TextField
          name="new-password"
          data-cy="new-password"
          id="new-password"
          label="New Password"
          variant="outlined"
          type="text"
          sx={{
            marginBottom: '0.5em',
            width: '100%',
            '& input': {
              WebkitTextSecurity: showNewPassword ? 'none' : 'disc',
              fontFamily: showNewPassword ? 'inherit' : 'caption',
            },
          }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            handleNewPasswordChange(event)
          }
          onMouseUp={handleNewPasswordCheck}
          onKeyUp={handleNewPasswordCheck}
          error={!newPasswordValid}
          helperText={!newPasswordValid ? newPasswordError : ''}
          slotProps={{
            input: {
              inputProps: {
                role: 'textbox',
                'aria-label': 'New Password',
              },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle new password visibility"
                    onClick={handleClickShowNewPassword}
                    data-cy="new-password-visibility"
                  >
                    {showNewPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        {renderStrengthMeter(newPassword)}
        <TextField
          name="repeat-new-password"
          data-cy="repeat-new-password"
          id="repeat-new-password"
          label="Repeat Password"
          variant="outlined"
          type="text"
          sx={{
            marginBottom: '0.5em',
            width: '100%',
            '& input': {
              WebkitTextSecurity: showRepeatPassword ? 'none' : 'disc',
              fontFamily: showRepeatPassword ? 'inherit' : 'caption',
            },
          }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            handleRepeatPasswordChange(event)
          }
          onMouseUp={handleRepeatPasswordCheck}
          onKeyUp={handleRepeatPasswordCheck}
          error={!repeatPasswordValid}
          helperText={!repeatPasswordValid ? repeatPasswordError : ''}
          slotProps={{
            input: {
              inputProps: {
                role: 'textbox',
                'aria-label': 'Repeat Password',
              },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle repeat password visibility"
                    onClick={handleClickShowRepeatPassword}
                    data-cy="repeat-password-visibility"
                  >
                    {showRepeatPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        <Button
          data-cy="new-password-submit-button"
          type="submit"
          specificColorToUse={primaryColor}
          role="button"
        >
          Submit
        </Button>

        <Grid container>
          <Grid size={6} style={{ padding: '1em 0 0 0' }}>
            <button
              type="button"
              onClick={handleCancel}
              className="as-link"
              data-cy="cancel-enter-new-password"
            >
              Cancel
            </button>
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
}
